/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import clsx from 'clsx';
import React, { forwardRef, Ref, useEffect, useState } from 'react';
import { DXP_ACTIVE_CLASS } from '../../Constants/Design';
import { ICON_CANCEL } from '../../Icons';
import { SdsIcon } from '../SdsIcon';
import { Tooltip, TOOLTIP_POSITION_TOP, TooltipProps } from '../Tooltip';
import { chipDefaults, ChipProps } from './Chip.props';
import { CHIP_ICON_LEFT, CHIP_ICON_RIGHT } from './types/ChipIcon.types';
import { CHIP_TYPE_NAVIGATION, CHIP_TYPE_REMOVABLE, CHIP_TYPE_SELECTION } from './types/ChipType.types';

/**
 * SDS Chip component.
 */
export const Chip = forwardRef((props: ChipProps, ref: Ref<HTMLDivElement>): JSX.Element => {
  const {
    classes,
    customClick,
    icon,
    iconColour,
    iconPosition,
    isSelected,
    text,
    theme,
    toggleTextDeselect,
    toggleTextRemove,
    toggleTextSelect,
    type,
    ...rest
  } = {
    ...chipDefaults,
    ...props,
  };

  const [chipWidth, setChipWidth] = useState(0);
  const [chipSelected, setSelected] = useState<boolean>(isSelected);
  const [remove, setRemove] = useState<boolean>(false);

  const onSelectChipClick = (event: React.MouseEvent): void => {
    customClick && customClick(event);
    setSelected(!chipSelected);
  };

  const onRemovableChipClick = (event: React.MouseEvent): void => {
    customClick && customClick(event);
    setRemove(true);
  };

  const onRemovableChipDelete = (event: React.KeyboardEvent): void => {
    if (event.key === 'Delete' || event.key === 'Backspace') {
      setRemove(true);
    }
  };

  useEffect(() => {
    const chipRef = ref as React.RefObject<HTMLDivElement>;

    if (chipRef.current && chipWidth === 0) {
      setChipWidth(chipRef.current.offsetWidth);
    }
  }, [chipWidth, ref]);

  if (remove) {
    return <></>;
  }

  const chipClass = clsx(`sds-chip`, `chip--${theme}`, classes, {
    [`chip--icon`]: !!icon,
    [`chip--navigation`]: type === CHIP_TYPE_NAVIGATION,
    [`chip--removable`]: type === CHIP_TYPE_REMOVABLE,
    [`chip--selection`]: type === CHIP_TYPE_SELECTION,
    [DXP_ACTIVE_CLASS]: chipSelected,
  });

  const tooltipLabel = text as string;
  const tooltipProps: TooltipProps = {
    label: tooltipLabel,
    position: TOOLTIP_POSITION_TOP,
  };

  const showTooltip = chipWidth >= 196 ? true : false;

  const iconContent = icon && <span className={clsx(`sds-chip-icon`, iconColour && `svg--${iconColour}`)}>{icon}</span>;

  // Default - CHIP_TYPE_NAVIGATION
  let chip = (
    <div className={chipClass} {...rest} ref={ref}>
      <button
        className="sds-chip-label"
        data-chip-type={CHIP_TYPE_NAVIGATION}
        onClick={(event): void => {
          event.preventDefault();
          customClick && customClick(event);
        }}
      >
        {iconPosition === CHIP_ICON_LEFT && iconContent}
        <span className="sds-chip-label--text">{text}</span>
        {iconPosition === CHIP_ICON_RIGHT && iconContent}
      </button>
    </div>
  );

  if (type === CHIP_TYPE_SELECTION) {
    const toggleText = `${text}. ${chipSelected ? toggleTextDeselect : toggleTextSelect}.`;

    chip = (
      <div className={chipClass} {...rest} ref={ref}>
        <button
          aria-label={toggleText}
          className="sds-chip-label"
          data-chip-type={CHIP_TYPE_SELECTION}
          onClick={(event): void => {
            event.preventDefault();
            onSelectChipClick(event);
          }}
        >
          {iconPosition === CHIP_ICON_LEFT && iconContent}
          <span className="sds-chip-label--text">{text}</span>
          {iconPosition === CHIP_ICON_RIGHT && iconContent}
        </button>
      </div>
    );
  }

  if (type === CHIP_TYPE_REMOVABLE) {
    const toggleText = `${text}. ${toggleTextRemove}.`;

    chip = (
      <li>
        <div className={chipClass} {...rest} ref={ref}>
          <span className="sds-chip-label">{text}</span>
          <button
            className="sds-chip-remover"
            data-chip-type={CHIP_TYPE_REMOVABLE}
            onClick={(event): void => {
              onRemovableChipClick(event);
            }}
            onKeyDown={(event): void => {
              onRemovableChipDelete(event);
            }}
            type="button"
          >
            <span className="sr-only">{toggleText}</span>
            <SdsIcon svg={ICON_CANCEL} />
          </button>
        </div>
      </li>
    );
  }

  if (showTooltip) {
    return <Tooltip {...tooltipProps}>{chip}</Tooltip>;
  }

  return chip;
});
